// Přehled značek A–Z (issue #32, screens/18-znacky.md, mockupy
// design/Značky.dc.html + design/Značky mobil.dc.html).
//
// Nativní DOM `/znacka/` (`body.type-manufacturers-listing`) je bohatší, než
// handoff čekal — Shoptet značky sám seskupuje po písmenech a renderuje kotevní
// lištu, takže „seskupení = JS enhancement" ze specky NEplatí:
//   main#content > h1.category-title
//                > ul.manufacturers-anchors > li > a[href="#B"]   (jen písmena s obsahem)
//                > ul.manufacturers > li > h2#B + ul > li > a[data-testid="brandLink"]
// Restylujeme tedy nativní strom a dopočítáváme jen to, co v něm chybí:
// prázdná písmena abecední lišty, wordmark boxy, panel vlastní značky,
// klientský filtr značek (mobil), SEO blok a poradnu.
//
// VYPUŠTĚNO proti designu (data na stránce nejsou, nevymýšlíme je): jednořádkové
// popisy značek, počty produktů u značky i v CTA vlastní značky a paginace —
// Shoptet vypisuje všechny značky na jednu stranu.
//
// Loga značek klient zatím nedodal → wordmark = název v bílém boxu (handoff §18).
// Po dodání log se mění jen obsah `.bs-brand-card__mark`, rozměry zůstávají.
//
// Idempotentní; bez JS zůstane funkční nativní seznam odkazů.

import { icon } from './icon.js';
import { buildAdvice } from './categoryPage.js';
import { page } from './pages.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/brandsPage.js';

const READY = 'bs-brands-ready';
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Vlastní značka shopu (`shop.brand` z manifestu) — panel se staví, jen když ji
// seznam opravdu obsahuje
const OWN_BRAND = new RegExp(SHOP.shop.brand, 'i');

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
// Perex, SEO blok i poradna jsou copy z designu (stránku Shoptet redakčně
// needituje) — před go-live projít s klientem stejně jako texty homepage (QA #41).
const LABEL_PEREX = L.perex;
const LABEL_FILTER_PLACEHOLDER = L.filterPlaceholder;
const LABEL_FILTER_NONE = L.filterNone;
const LABEL_OWN_EYEBROW = L.ownEyebrow;
const LABEL_OWN_TITLE = L.ownTitle;
const LABEL_OWN_TEXT = L.ownText;
const LABEL_OWN_CTA_PRE = L.ownCtaPre;
const LABEL_SEO_TITLE = L.seoTitle;
const LABEL_SEO_TEXT = L.seoText;
const SEO_POINTS = L.seoPoints;

// Poradna (§4) — na rozdíl od výpisu kategorie světlá karta, sdílená komponenta
// `buildAdvice` ji umí přes modifikátor `.bs-advice--light`.
const ADVICE = {
  icon: 'headset',
  title: L.adviceTitle,
  text: L.adviceText,
  // se zapnutým chatem je první volbou chat (buildAdvice, chatActions)
  actions: [{ chat: 'brands', primary: true }, { href: page('contact'), label: L.adviceAction, primary: true }],
};

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function isBrands() {
  return document.body.classList.contains('type-manufacturers-listing');
}

// 1 značka / 2–4 značky / 5+ značek
export function brandsWord(n) {
  return L.brandsWord(n);
}

function text(node) {
  return ((node && node.textContent) || '').replace(/\s+/g, ' ').trim();
}

// Porovnání bez diakritiky a velikosti písmen (GÜDE vs. gude)
function fold(s) {
  const raw = (s || '').toLowerCase();
  if (!raw.normalize) return raw;
  // NFD rozpadne diakritiku na kombinující znaky U+0300–U+036F, ty zahodíme
  const dec = raw.normalize('NFD');
  let out = '';
  for (let n = 0; n < dec.length; n += 1) {
    const code = dec.charCodeAt(n);
    if (code < 768 || code > 879) out += dec.charAt(n);
  }
  return out;
}

function brandLinks() {
  return Array.prototype.slice.call(
    document.querySelectorAll('.manufacturers a[data-testid="brandLink"], .manufacturers li > a[href^="' + page('brands') + '"]')
  );
}

// ---------------------------------------------------------------------------
// Hlavička: H1 + počet značek + perex (§„Přehled značek")
// ---------------------------------------------------------------------------
function enhanceHeading() {
  const h1 = document.querySelector('#content h1.category-title') || document.querySelector('#content h1');
  if (!h1 || h1.closest('.bs-cat-head')) return;

  // sdílená hlavička výpisu (#13) — stejná typografie i baseline počtu
  const head = el('div', 'bs-cat-head');
  h1.insertAdjacentElement('beforebegin', head);
  head.appendChild(h1);

  const total = brandLinks().length;
  if (total) head.appendChild(el('span', 'bs-cat-count', total + ' ' + brandsWord(total)));

  head.insertAdjacentElement('afterend', el('p', 'bs-brands__perex', LABEL_PEREX));
}

// ---------------------------------------------------------------------------
// Klientský filtr značek (mobil) — CSS ho nad $bp-md schová, tam vede abeceda
// ---------------------------------------------------------------------------
function applyFilter(query) {
  const content = document.querySelector('#content');
  const q = fold(query).trim();
  let hits = 0;

  Array.prototype.forEach.call(document.querySelectorAll('.bs-brands__group'), function (group) {
    let shown = 0;
    Array.prototype.forEach.call(group.querySelectorAll('.bs-brands__item'), function (item) {
      const name = fold(item.getAttribute('data-bs-brand') || '');
      const match = !q || name.indexOf(q) !== -1;
      item.classList.toggle('is-hidden', !match);
      if (match) shown += 1;
    });
    group.classList.toggle('is-hidden', shown === 0);
    hits += shown;
  });

  if (content) content.classList.toggle('is-filtering', !!q);
  const none = document.querySelector('.bs-brands__none');
  if (none) none.classList.toggle('is-visible', !!q && hits === 0);
}

function buildFilter(anchor) {
  if (document.querySelector('.bs-brands__filter')) return;

  const box = el('div', 'bs-brands__filter');
  box.appendChild(icon('search'));

  const input = document.createElement('input');
  input.type = 'search';
  input.className = 'bs-brands__filter-input';
  input.placeholder = LABEL_FILTER_PLACEHOLDER;
  input.setAttribute('aria-label', LABEL_FILTER_PLACEHOLDER);
  input.autocomplete = 'off';
  input.addEventListener('input', function () {
    applyFilter(input.value);
  });
  box.appendChild(input);

  anchor.insertAdjacentElement('afterend', box);
}

// ---------------------------------------------------------------------------
// Abecední lišta — nativní kotvy doplněné o prázdná písmena (§1)
// ---------------------------------------------------------------------------
function enhanceAnchors() {
  const list = document.querySelector('ul.manufacturers-anchors');
  if (!list || list.dataset.bsAlpha) return null;
  list.dataset.bsAlpha = '1';
  list.classList.add('bs-alpha');

  // nativní položky podle písmene; co není v A–Z (číslice, „Ø"), zůstane na konci
  const found = {};
  const extra = [];
  Array.prototype.forEach.call(list.children, function (li) {
    const letter = text(li).toUpperCase();
    li.classList.add('bs-alpha__item', 'is-on');
    if (ALPHABET.indexOf(letter) !== -1) found[letter] = li;
    else extra.push(li);
  });

  ALPHABET.forEach(function (letter) {
    if (found[letter]) {
      list.appendChild(found[letter]);
      return;
    }
    const li = el('li', 'bs-alpha__item is-off');
    li.appendChild(el('span', null, letter));
    li.setAttribute('aria-hidden', 'true');
    list.appendChild(li);
  });
  extra.forEach(function (li) {
    list.appendChild(li);
  });

  return list;
}

// ---------------------------------------------------------------------------
// Panel vlastní značky (§2)
// ---------------------------------------------------------------------------
function buildOwnBrand(anchor) {
  if (!anchor || document.querySelector('.bs-brands__own')) return;

  const own = brandLinks().filter(function (a) {
    return OWN_BRAND.test(text(a));
  })[0];
  if (!own) return;

  const name = text(own);
  const card = el('a', 'bs-brands__own');
  card.href = own.getAttribute('href') || '#';

  card.appendChild(el('span', 'bs-brands__own-mark', name));

  const body = el('span', 'bs-brands__own-body');
  body.appendChild(el('span', 'bs-brands__own-eyebrow', LABEL_OWN_EYEBROW));
  body.appendChild(el('span', 'bs-brands__own-title', LABEL_OWN_TITLE));
  body.appendChild(el('span', 'bs-brands__own-text', LABEL_OWN_TEXT));

  const cta = el('span', 'bs-brands__own-cta');
  cta.appendChild(el('span', null, LABEL_OWN_CTA_PRE + name));
  cta.appendChild(icon('arrow-right'));
  body.appendChild(cta);

  card.appendChild(body);
  anchor.insertAdjacentElement('afterend', card);
}

// ---------------------------------------------------------------------------
// Skupiny po písmenech + karty značek (§3)
// ---------------------------------------------------------------------------
function enhanceCard(link) {
  if (link.dataset.bsBrandCard) return;
  link.dataset.bsBrandCard = '1';

  const name = text(link);
  link.classList.add('bs-brand-card');
  link.textContent = '';
  link.appendChild(el('span', 'bs-brand-card__mark', name));
  link.appendChild(el('span', 'bs-brand-card__name', name));

  const item = link.parentElement;
  if (item && item.tagName === 'LI') {
    item.classList.add('bs-brands__item');
    item.setAttribute('data-bs-brand', name);
  }
}

function enhanceGroups() {
  const list = document.querySelector('ul.manufacturers');
  if (!list) return null;
  list.classList.add('bs-brands__groups');

  Array.prototype.forEach.call(list.children, function (group) {
    group.classList.add('bs-brands__group');

    const grid = group.querySelector('ul');
    if (grid) grid.classList.add('bs-brands__grid');

    const links = Array.prototype.slice.call(group.querySelectorAll('a[href^="' + page('brands') + '"]'));
    links.forEach(enhanceCard);

    const head = group.querySelector('h2');
    if (head && !head.dataset.bsGroupHead) {
      head.dataset.bsGroupHead = '1';
      const letter = text(head);
      head.textContent = '';
      head.classList.add('bs-brands__ghead');
      head.appendChild(el('span', 'bs-brands__gletter', letter));
      head.appendChild(el('span', 'bs-brands__grule'));
      head.appendChild(el('span', 'bs-brands__gcount', links.length + ' ' + brandsWord(links.length)));
    }
  });

  if (!document.querySelector('.bs-brands__none')) {
    list.insertAdjacentElement('afterend', el('p', 'bs-brands__none', LABEL_FILTER_NONE));
  }

  return list;
}

// ---------------------------------------------------------------------------
// SEO blok + poradna (§4) — sdílí mřížku i typografii s výpisem kategorie
// ---------------------------------------------------------------------------
function buildBottom() {
  const host = document.querySelector('#content');
  if (!host || host.querySelector('.bs-cat-bottom')) return;

  const bottom = el('div', 'bs-cat-bottom');

  const seo = el('div', 'bs-cat-seo');
  seo.appendChild(el('h2', null, LABEL_SEO_TITLE));
  seo.appendChild(el('p', null, LABEL_SEO_TEXT));

  const points = el('div', 'bs-brands__adv');
  SEO_POINTS.forEach(function (p) {
    const row = el('div', 'bs-brands__adv-row');
    const mark = el('span', 'bs-brands__adv-icon');
    mark.appendChild(icon('check'));
    row.appendChild(mark);
    const body = el('div', 'bs-brands__adv-text');
    body.appendChild(el('b', null, p.title));
    body.appendChild(document.createTextNode(': ' + p.text));
    row.appendChild(body);
    points.appendChild(row);
  });
  seo.appendChild(points);
  bottom.appendChild(seo);

  const advice = buildAdvice(ADVICE);
  advice.classList.add('bs-advice--light');
  bottom.appendChild(advice);

  host.appendChild(bottom);
}

// ---------------------------------------------------------------------------

export function initBrandsPage() {
  if (!isBrands()) return;
  if (!document.querySelector('#content .manufacturers')) return;

  enhanceHeading();
  const alpha = enhanceAnchors();
  const head = document.querySelector('.bs-brands__perex') || document.querySelector('.bs-cat-head');
  if (head) buildFilter(head);
  buildOwnBrand(alpha || document.querySelector('.bs-brands__filter') || head);
  enhanceGroups();
  buildBottom();

  document.documentElement.classList.add(READY);
}
